'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetMarket } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { Panel } from '@/components/ui/panel';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { cn } from '@/lib/ui/utils';
import { FeaturedHeader, type FeaturedHeroCarouselNavProps } from './featured-header';
import type { HeroMarket } from './hero-market';
import type { OutcomeDecorParams } from './outcome-decor';
import { OutcomeSparklineGrid } from './outcome-sparkline-grid';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type FeaturedMarketHeroCarouselProps = FeaturedHeroCarouselNavProps;
export interface FeaturedMarketHeroProps {
  market: HeroMarket;
  /** Carousel slot index — passed for telemetry. */
  slot: number;
  className?: string;
  carousel?: FeaturedMarketHeroCarouselProps;
  /** Page-owned market watchlist relationship; avoids a remount-local query. */
  watchlistPubkeys?: readonly string[] | undefined;
  /** Workspace-only — forwarded to the outcome cards' decorative image override. */
  decorOverride?: OutcomeDecorParams;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Featured market hero — desktop-only top-of-feed widget that pins a single
 * editorially-promoted market with a richer chrome than `<MarketRowCard>`.
 *
 * Outcome percentages and header meta (thesis count, TVL, creator) are
 * NATS-live. The hero owns ONE `useGetMarket` detail subscription here; the
 * header and sparkline grid read their individual live fields off that single
 * cache entry via `useGetMarketField`. The hero takes the static `HeroMarket`
 * snapshot for first paint.
 *
 * Hidden on mobile (`hidden md:flex`) — the hero never renders below `md`.
 */
export function FeaturedMarketHero(props) {
  const $ = _c(30);
  const {
    market,
    slot,
    className,
    carousel,
    decorOverride,
    watchlistPubkeys
  } = props;
  const tFeatured = useTranslations("markets.featured");
  const href = `/markets/${market.pubkey}`;
  const intent = useIntentRoutePrefetch(href);
  let t0;
  if ($[0] !== market.pubkey) {
    t0 = {
      pubkey: market.pubkey
    };
    $[0] = market.pubkey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useGetMarket(t0);
  let t1;
  if ($[2] !== className) {
    t1 = cn("relative hidden md:flex h-full w-full flex-col gap-4 overflow-hidden p-5 cursor-pointer", "[--chart-surface-dot-pitch:10px] [--chart-surface-dot-inner:1px] [--chart-surface-dot-outer:1.75px]", "dark:[--pattern-dot-grid-color:var(--gray-700)]", className);
    $[2] = className;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== market.title || $[5] !== tFeatured) {
    t2 = tFeatured("title_aria", {
      title: market.title
    });
    $[4] = market.title;
    $[5] = tFeatured;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== href || $[8] !== intent || $[9] !== market.title || $[10] !== t2) {
    t3 = <Link href={href} prefetch={false} {...intent} aria-label={t2} title={market.title} data-testid="featured-market-hero-link" className="absolute inset-0 z-0 rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-border-primary" />;
    $[7] = href;
    $[8] = intent;
    $[9] = market.title;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== carousel) {
    t4 = carousel !== undefined ? {
      carousel
    } : {};
    $[12] = carousel;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== market || $[15] !== slot || $[16] !== t4 || $[17] !== watchlistPubkeys) {
    t5 = <FeaturedHeader market={market} slot={slot} watchlistPubkeys={watchlistPubkeys} {...t4} />;
    $[14] = market;
    $[15] = slot;
    $[16] = t4;
    $[17] = watchlistPubkeys;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== decorOverride) {
    t6 = decorOverride !== undefined ? {
      decorOverride
    } : {};
    $[19] = decorOverride;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== market || $[22] !== slot || $[23] !== t6) {
    t7 = <OutcomeSparklineGrid market={market} slot={slot} className="mt-2" {...t6} />;
    $[21] = market;
    $[22] = slot;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== t1 || $[26] !== t3 || $[27] !== t5 || $[28] !== t7) {
    t8 = <Panel surface="solid" elevation="flat" className={t1} data-testid="featured-market-hero">{t3}{t5}{t7}</Panel>;
    $[25] = t1;
    $[26] = t3;
    $[27] = t5;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return t8;
}